import { memo, useMemo, useState } from 'react';

import {
  createFaviconLoadGuard,
  getAvatarInitials,
  getGravatarUrl,
} from '@/lib/crm/utils/avatar';

import type {
  GmailSendPayload,
  ProposalDetail,
  ProposalListItem,
} from '../../lib/proposals/core/types';
import { getActionMetadata } from '../../lib/proposals/registry';
import {
  getPrimaryAction,
  isGmailSendAction,
} from '../../lib/proposals/utils/helpers';
import CategoryIcon from './CategoryIcon';

interface RecipientAvatarProps {
  action: ProposalDetail | ProposalListItem;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
}

interface RecipientInfo {
  email: string;
  domain: string;
  name: string;
}

// Size classes — static, hoisted to module scope to avoid recreation on every render
const sizeClasses = {
  xxs: 'w-5 h-5 text-[7px]',
  xs: 'w-[26px] h-[26px] text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-14 h-14 text-base',
};

const stackedSizeClasses = {
  xxs: 'w-[14px] h-[14px] text-[6px]',
  xs: 'w-[22px] h-[22px] text-[8px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-xs',
  lg: 'w-11 h-11 text-sm',
};

const overflowSizeClasses = {
  xxs: 'w-3.5 h-3.5 text-[6px]',
  xs: 'w-5 h-5 text-[8px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-9 h-9 text-xs',
};

// Overlap for the 2nd stacked avatar and the overflow bubble, scaled per size.
// The previous single hardcoded -ml-4 (16px) exceeded the 14px xxs avatar,
// pulling the 2nd avatar outside its own box and collapsing the cluster.
const stackedOverlapClasses = {
  xxs: '-ml-2', // 8px on 14px avatars -> cluster 20px, 2nd avatar left edge 6px (fully inside)
  xs: '-ml-4', // 16px on 22px avatars (unchanged: no paint-outside at this width)
  sm: '-ml-4',
  md: '-ml-4',
  lg: '-ml-4',
};

const iconSizeClasses = {
  xxs: 'w-2 h-2', // 8px = 50% of 16px
  xs: 'w-3 h-3', // 12px = 46.2% of 26px
  sm: 'w-4 h-4', // 16px = 50.0% of 32px (unchanged)
  md: 'w-[22px] h-[22px]', // 22px = 45.8% of 48px
  lg: 'w-[26px] h-[26px]', // 26px = 46.4% of 56px
};

function RecipientAvatar({ action, size = 'md' }: RecipientAvatarProps) {
  // Primary avatar: gravatar -> google favicon -> initials
  // (DuckDuckGo removed - returns generic arrow icons for unknown domains)
  const [logoAttempt, setLogoAttempt] = useState<
    'gravatar' | 'google' | 'failed'
  >('gravatar');
  // Secondary avatar (for stacked view): google favicon -> initials
  const [secondLogoAttempt, setSecondLogoAttempt] = useState<
    'google' | 'failed'
  >('google');

  // Memoize primary action to prevent recreation on every render
  const primaryAction = useMemo(() => {
    if ('actions' in action) {
      return getPrimaryAction(action);
    }
    return undefined;
  }, [action]);

  // When there's no email recipient, fall back to the action-type registry icon
  // (the same source the action cards use, e.g. create_pipeline → PipelineIcon)
  // before the coarse filter-tag CategoryIcon. Only resolvable for ProposalDetail
  // (which carries the actions array); list items have no primary action.
  const RegistryIcon = useMemo(
    () =>
      primaryAction ? (getActionMetadata(primaryAction)?.icon ?? null) : null,
    [primaryAction]
  );

  // Extract primary filter tag for icon fallback
  const primaryTag = action.filter_tags?.[0] ?? 'all';

  // Extract all recipient info from gmail action payload - memoized to avoid
  // recalculating email parsing on every render
  const recipientInfoList = useMemo((): RecipientInfo[] => {
    // For ProposalListItem, use primary_recipient_email from API
    if ('primary_recipient_email' in action && action.primary_recipient_email) {
      const email = action.primary_recipient_email;
      const domain = email.split('@')[1];
      const name = email
        .split('@')[0]
        .replace(/[._]/g, ' ')
        .replace(/\b\w/g, (l: string) => l.toUpperCase());
      return [{ email, domain, name }];
    }

    // For ProposalDetail, extract from actions array
    if (primaryAction && isGmailSendAction(primaryAction)) {
      const gmailPayload = primaryAction.payload as GmailSendPayload;
      const toAddresses = gmailPayload?.action_params?.to;
      if (toAddresses?.length) {
        return toAddresses.map(email => {
          const domain = email.split('@')[1];
          const name = email
            .split('@')[0]
            .replace(/[._]/g, ' ')
            .replace(/\b\w/g, (l: string) => l.toUpperCase());
          return { email, domain, name };
        });
      }
    }
    return [];
  }, [action, primaryAction]);
  const isGroup = recipientInfoList.length > 1;
  const primaryRecipient = recipientInfoList[0] || null;
  const secondaryRecipient = recipientInfoList[1] || null;
  const overflowCount = Math.max(0, recipientInfoList.length - 2);

  const sizeClass = sizeClasses[size];
  const stackedSizeClass = stackedSizeClasses[size];
  const overflowSizeClass = overflowSizeClasses[size];
  const stackedOverlapClass = stackedOverlapClasses[size];

  // Size mapping for Gravatar and favicon APIs (16, 32, 64, 128, 256)
  // Use higher resolution for better quality
  const pixelSizeMap = { xxs: 32, xs: 64, sm: 128, md: 256, lg: 256 } as const;
  const pixelSize = pixelSizeMap[size];

  // Handle logo loading errors — try gravatar, then google favicon, then initials
  const handleLogoError = () => {
    if (logoAttempt === 'gravatar') {
      setLogoAttempt('google');
    } else if (logoAttempt === 'google') {
      setLogoAttempt('failed');
    }
  };

  // Handle second avatar logo errors (for stacked view)
  const handleSecondLogoError = () => {
    if (secondLogoAttempt === 'google') {
      setSecondLogoAttempt('failed');
    }
  };

  // Get logo URL for the primary avatar — gravatar, then google favicon
  const getLogoUrl = (
    recipientInfo: RecipientInfo,
    attempt: string
  ): string | null => {
    if (attempt === 'gravatar') {
      return getGravatarUrl(recipientInfo.email, pixelSize);
    } else if (attempt === 'google') {
      return `https://www.google.com/s2/favicons?domain=${recipientInfo.domain}&sz=${pixelSize}`;
    }
    return null;
  };

  // Render single avatar (used for both single recipient and stacked components)
  const renderSingleAvatar = (
    recipientInfo: RecipientInfo | null,
    attempt: string,
    onError: () => void,
    avatarSizeClass: string,
    zIndex?: string,
    marginClass?: string
  ) => {
    if (!recipientInfo) {
      return (
        <div
          className={`${avatarSizeClass} rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white font-semibold flex-shrink-0 ${zIndex || ''} ${marginClass || ''}`}
        >
          {RegistryIcon ? (
            <RegistryIcon className={iconSizeClasses[size]} />
          ) : (
            <CategoryIcon
              category={primaryTag}
              className={iconSizeClasses[size]}
            />
          )}
        </div>
      );
    }

    const logoUrl = getLogoUrl(recipientInfo, attempt);

    if (logoUrl && attempt !== 'failed') {
      // Use rounded-md for favicon attempts (company icons look better square)
      // Use rounded-full for gravatar (person photos)
      const isFavicon = attempt === 'google';
      const borderRadius = isFavicon ? 'rounded-md' : 'rounded-full';

      return (
        <div
          className={`${avatarSizeClass} ${borderRadius} overflow-hidden flex items-center justify-center bg-primary border border-primary flex-shrink-0 ${zIndex || ''} ${marginClass || ''}`}
        >
          <img
            src={logoUrl}
            alt={recipientInfo.domain}
            className='w-full h-full object-contain p-0.5'
            onLoad={isFavicon ? createFaviconLoadGuard(onError) : undefined}
            onError={onError}
          />
        </div>
      );
    }

    // Show initials fallback
    return (
      <div
        className={`${avatarSizeClass} rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white font-semibold flex-shrink-0 ${zIndex || ''} ${marginClass || ''}`}
      >
        {getAvatarInitials(recipientInfo.name)}
      </div>
    );
  };

  // Render second avatar (company favicon only, no Gravatar)
  const renderSecondAvatar = (
    recipientInfo: RecipientInfo | null,
    attempt: string,
    onError: () => void,
    avatarSizeClass: string,
    zIndex?: string,
    marginClass?: string
  ) => {
    if (!recipientInfo) {
      return null;
    }

    let logoUrl: string | null = null;
    if (attempt === 'google') {
      logoUrl = `https://www.google.com/s2/favicons?domain=${recipientInfo.domain}&sz=${pixelSize}`;
    }

    if (logoUrl && attempt !== 'failed') {
      // Use square styling for favicons (like CrmObjectAvatar)
      return (
        <div
          className={`${avatarSizeClass} rounded-md overflow-hidden flex items-center justify-center flex-shrink-0 ${zIndex || ''} ${marginClass || ''}`}
        >
          <img
            src={logoUrl}
            alt={recipientInfo.domain}
            className='w-full h-full object-contain'
            onLoad={
              attempt === 'google' ? createFaviconLoadGuard(onError) : undefined
            }
            onError={onError}
          />
        </div>
      );
    }

    // Show initials fallback
    return (
      <div
        className={`${avatarSizeClass} rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white font-semibold flex-shrink-0 ${zIndex || ''} ${marginClass || ''}`}
      >
        {getAvatarInitials(recipientInfo.name)}
      </div>
    );
  };

  // Render stacked avatars for group emails
  if (isGroup) {
    return (
      <div
        className='flex items-center'
        data-component='stacked-avatars'
        data-testid='stacked-avatars'
      >
        {renderSingleAvatar(
          primaryRecipient,
          logoAttempt,
          handleLogoError,
          stackedSizeClass,
          'relative z-20',
          ''
        )}
        {renderSecondAvatar(
          secondaryRecipient,
          secondLogoAttempt,
          handleSecondLogoError,
          stackedSizeClass,
          'relative z-10',
          stackedOverlapClass
        )}
        {overflowCount > 0 && (
          <div
            className={`${overflowSizeClass} rounded-full bg-base-300 border border-base-content/20 flex items-center justify-center text-base-content font-medium flex-shrink-0 relative z-0 ${stackedOverlapClass}`}
          >
            +{overflowCount}
          </div>
        )}
      </div>
    );
  }

  // Single recipient view
  return renderSingleAvatar(
    primaryRecipient,
    logoAttempt,
    handleLogoError,
    sizeClass
  );
}

// Memoize to prevent unnecessary re-renders in virtualized lists
export default memo(RecipientAvatar);
